import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Timestamp, doc, getDoc, setDoc } from 'firebase/firestore';
import { useLocalStorage } from 'usehooks-ts';

import { db } from '~/config/firebase.config';
import { forbiddenWords } from '~/config/forbiddenWords';
import { generateFruityUsername } from '~/config/fruitsUsername';
import { PlayerData } from '~/types/player/player';
import { createUUID } from '~/utils/uuid';
import { AvatarCarousel } from '~/components/AvatarCarousel';
import { PlayerLanguagePicker } from '~/components/LanguageSelector';
import { useVenueLanguagePicker } from '~/stores/useVenueLanguagePicker';
import { showsLanguagePicker } from '~/utils/playerLanguage';
import { PrivacyNotice } from '~/components/PrivacyNotice';
import { AvatarCredit } from '~/components/AvatarCredit/AvatarCredit';
import { BrandLogo } from '~/components/branding/BrandLogo';
import {
    cleanVenueName,
    newsletterConsentWrite,
    savedConsentFromRecord,
    type SavedConsent,
    startsWithVowel,
} from '~/utils/playerConsent';

import styles from './ProfileSetting.module.css';
import { JoinGame } from '../JoinGame';

const SELECT_OPTIONS: Record<string, readonly string[]> = {
    gender: ['male', 'female', 'other'],
    travelWith: ['alone', 'couple', 'friends', 'family', 'other'],
    age: ['under_12', '12_17', '18_24', '25_34', '35_49', '50_64', '65_plus'],
};

export type ProfileSettingProps = {
    onSubmitted: (player: any) => void;
    clientID?: string;
    userData?: any;
};

export function ProfileSetting({ onSubmitted, clientID, userData }: ProfileSettingProps) {
    const { t } = useTranslation();
    const canChooseUsername = clientID !== 'airport-ORY';
    // Drapeaux de langue : aéroports (écran du pseudo seulement) et lieux qui ont allumé
    // l'option dans Mon compte. Ailleurs, rien de plus (le téléphone suit déjà sa langue).
    const venueAllows = useVenueLanguagePicker((s) => s.venueAllows);
    const showProfilePicker = showsLanguagePicker(clientID, venueAllows, 'profile');
    const showRejoinPicker = showsLanguagePicker(clientID, venueAllows, 'rejoin');

    const [clients, setClients] = useLocalStorage<string[]>('clients', []);
    const [optionData, setOptionData] = useState<string[]>([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [username, setUsername] = useState<string>(
        canChooseUsername ? userData?.username || '' : generateFruityUsername()
    );
    const [usernameError, setUsernameError] = useState<string>('');
    const [newsletterConsent, setNewsletterConsent] = useState(false);
    // La case part de ce que le joueur a déjà répondu à CET établissement (fiche
    // Firestore), jamais du localStorage : celui-ci est commun à tous les lieux.
    // Voir newsletterConsentWrite pour ce qu'on enregistre selon cet état.
    const [savedConsent, setSavedConsent] = useState<SavedConsent>(() =>
        clientID && userData?.uid ? 'pending' : false
    );
    const consentTouched = useRef(false);
    // Nom affiché dans la case newsletter. Lu dans le même document public que la
    // config des champs (la manette n'a pas le droit de lire clients/{id}) ; null
    // tant qu'il n'est pas chargé, ou s'il manque → formulation neutre.
    const [venueName, setVenueName] = useState<string | null>(null);
    const [selectedAvatar, setSelectedAvatar] = useState<string>(userData?.avatar || '');

    const askData =
        (clientID && !clients.includes(clientID)) || optionData.some((key) => !Object.keys(userData).includes(key));

    // Skip the carousel's initial onChange; afterwards a new avatar regenerates the
    // fruity username for the no-username flow (airport-ORY), as before.
    const avatarInitialised = useRef(false);
    const onAvatarChange = (next: string) => {
        setSelectedAvatar(next);
        if (!avatarInitialised.current) {
            avatarInitialised.current = true;
            return;
        }
        if (!canChooseUsername) {
            setUsername(generateFruityUsername());
            setUsernameError('');
        }
    };

    useEffect(() => {
        if (!clientID) return;
        const clientRef = doc(db, 'clients', clientID, 'public', 'userData');
        getDoc(clientRef)
            .then((docSnapshot) => {
                if (docSnapshot.exists()) {
                    const data = docSnapshot.data();
                    if (data) {
                        setVenueName(cleanVenueName(data.venueName));
                        const keys = Object.keys(data).reduce((acc: string[], key: string) => {
                            if (typeof data[key] === 'boolean' && key !== 'username' && data[key]) {
                                acc.push(key);
                            }
                            return acc;
                        }, []);
                        setOptionData(keys.sort());
                    }
                }
            })
            .catch((error) => {
                console.error('Failed to load user data config:', error);
            });
    }, [clientID]);

    // Lue même quand la case n'est pas affichée (établissement qui ne demande pas
    // l'email) : sans la connaître, on ne pourrait pas réécrire la fiche en entier
    // sans risquer d'effacer un « oui » donné quand l'email était demandé.
    useEffect(() => {
        const uid = userData?.uid;
        consentTouched.current = false;
        setNewsletterConsent(false);
        if (!clientID || !uid) {
            setSavedConsent(false);
            return undefined;
        }
        setSavedConsent('pending');
        if (!askData) return undefined;
        let cancelled = false;
        getDoc(doc(db, 'clients', clientID, 'userData', uid))
            .then((snap) => {
                if (cancelled) return;
                const saved = savedConsentFromRecord(snap.exists() ? snap.data() : undefined);
                setSavedConsent(saved);
                if (!consentTouched.current) setNewsletterConsent(saved);
            })
            .catch((error) => {
                console.error('Failed to load saved newsletter consent:', error);
                if (!cancelled) setSavedConsent('unknown');
            });
        return () => {
            cancelled = true;
        };
    }, [clientID, userData?.uid, askData]);

    const normalize = (text: string) =>
        (text || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');

    const validateUsername = (value: string) => {
        if (!value || value.length < 3) {
            return t('global.settings.error.invalidUsername');
        }
        const normalizedValue = normalize(value);
        if (forbiddenWords.some((word) => normalizedValue.includes(normalize(word)))) {
            return t('global.settings.error.forbiddenUsername');
        }
        return '';
    };

    const onChangeUsername: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const { value } = e.target;
        setUsername(value);
        setUsernameError(validateUsername(value));
        if (errorMessage) setErrorMessage('');
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        const uid = userData.uid || createUUID();

        setErrorMessage('');
        let errorMsg = '';
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const updatedUserData: PlayerData = {
            username: (formData.get('username') as string) || username,
            uid,
            avatar: selectedAvatar,
            termsConsent: true,
        };
        const consentWrite = newsletterConsentWrite({
            checked: newsletterConsent,
            touched: consentTouched.current,
            saved: savedConsent,
        });

        errorMsg = validateUsername(updatedUserData.username || '');

        for (const key of optionData) {
            const value = formData.get(key) as string;
            if (value) {
                if (key === 'email') {
                    if (!/\S+@\S+\.\S+/.test(value)) {
                        errorMsg = t('global.settings.error.invalidEmail');
                        break;
                    }
                    updatedUserData[key as keyof PlayerData] = value;
                } else {
                    updatedUserData[key as keyof PlayerData] = value;
                }
            } else {
                errorMsg = `${t('global.settings.error.fill')} ${key}`;
                break;
            }
        }

        if (errorMsg) {
            setErrorMessage(errorMsg);
        } else {
            if (optionData.length > 0 && clientID) {
                const userDocRef = doc(db, 'clients', clientID, 'userData', uid);

                try {
                    const record = {
                        ...updatedUserData,
                        ...(consentWrite.value === undefined ? {} : { newsletterConsent: consentWrite.value }),
                        date: Timestamp.now(),
                    };
                    // Remplacement quand on connaît le consentement enregistré (comme
                    // avant) ; fusion sinon, pour ne pas effacer un « oui » inconnu.
                    await (consentWrite.merge
                        ? setDoc(userDocRef, record, { merge: true })
                        : setDoc(userDocRef, record));
                } catch (error) {
                    console.error('Error saving user data: ', error);
                    setErrorMessage(t('global.settings.error.saveFailed') || 'Failed to save data. Please try again.');
                    return;
                }
            }
            if (clientID && !clients.includes(clientID)) {
                setClients((state) => [...state, clientID]);
            }
            onSubmitted?.(updatedUserData);
        }
    };

    if (!askData) {
        return (
            <JoinGame
                username={userData.username}
                onClick={() => onSubmitted(userData)}
                languagePicker={showRejoinPicker ? <PlayerLanguagePicker clientID={clientID} screen="rejoin" /> : null}
            />
        );
    }

    return (
        <form className={styles.container} onSubmit={handleSubmit}>
            <div className={styles.topbar}>
                <BrandLogo className={styles.logo} clientID={clientID} />
            </div>

            <div className={styles.inner}>
                {showProfilePicker && <PlayerLanguagePicker clientID={clientID} screen="profile" />}
                <AvatarCarousel initialAvatar={userData?.avatar} onChange={onAvatarChange} />

                {canChooseUsername ? (
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="username">
                            {t('global.settings.username')}
                        </label>
                        <input
                            id="username"
                            name="username"
                            className={`${styles.input} ${usernameError ? styles.error : ''}`}
                            value={username}
                            onChange={onChangeUsername}
                            placeholder={t('global.settings.usernamePlaceholder')}
                            required
                        />
                        {usernameError && <p className={styles.helper}>{usernameError}</p>}
                    </div>
                ) : (
                    <div className={styles.generatedName}>{username}</div>
                )}

                {optionData.map((key) => {
                    const selectOptions = SELECT_OPTIONS[key];
                    return (
                        <div className={styles.field} key={key}>
                            <label className={styles.label} htmlFor={key}>
                                {selectOptions
                                    ? t(`global.settings.${key}`)
                                    : `${t('global.settings.your')} ${t(`global.settings.${key}`)}`}
                            </label>
                            {selectOptions ? (
                                <select
                                    id={key}
                                    name={key}
                                    defaultValue={userData?.[key] ?? ''}
                                    className={styles.select}
                                >
                                    <option value="" disabled>
                                        {t('global.settings.selectPlaceholder')}
                                    </option>
                                    {selectOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {t(`global.settings.${key}Option.${option}`)}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    id={key}
                                    name={key}
                                    defaultValue={userData?.[key]}
                                    className={styles.input}
                                    placeholder={`${t('global.settings.enterYour')} ${t(`global.settings.${key}`).toLowerCase()}`}
                                />
                            )}
                        </div>
                    );
                })}

                {optionData.includes('email') && (
                    <label className={styles.consent}>
                        <input
                            type="checkbox"
                            name="newsletterConsent"
                            className={styles.checkbox}
                            checked={newsletterConsent}
                            onChange={() => {
                                consentTouched.current = true;
                                setNewsletterConsent((prev) => !prev);
                            }}
                        />
                        <span>
                            {venueName
                                ? t('playerConsent.newsletterVenue', {
                                      venue: venueName,
                                      context: startsWithVowel(venueName) ? 'vowel' : undefined,
                                  })
                                : t('playerConsent.newsletterFallback')}
                        </span>
                    </label>
                )}

                {errorMessage && <p className={styles.error}>{errorMessage}</p>}

                <button type="submit" className={styles.cta} disabled={!!usernameError}>
                    {t('global.settings.save')}
                </button>

                {/* Un seul bloc : l'espacement du formulaire ne s'applique qu'une fois aux deux mentions. */}
                <div>
                    <PrivacyNotice className={styles.terms} />
                    <AvatarCredit className={styles.terms} />
                </div>
            </div>
        </form>
    );
}
